/*
 * Project: bellcloud
 * File: theme/GlobalStyles.tsx
 * Description: Global CSS Reset & Base Styles ONLY
 *
 * ⚠️ WARNING: DO NOT add individual component styles here!
 *
 * 이 파일은 전역 리셋과 기본 HTML 요소 스타일만 포함합니다.
 * 개별 스타일링은 절대 여기에 추가하지 마세요!
 *
 * ❌ 여기에 추가하면 안 되는 것들:
 * - 색상, 간격, 폰트 크기 등의 디자인 토큰
 * - 컴포넌트별 스타일
 * - 페이지별 스타일
 * - CSS 변수
 *
 * ✅ 대신 이렇게 하세요:
 * - 디자인 토큰 변경 → theme/tokens/ 폴더
 * - 컴포넌트 스타일 → ui/components/ 폴더
 * - 페이지 스타일 → 각 페이지의 styled-components
 *
 * 이 파일은 "설정하고 잊어버리는(set and forget)" 파일입니다.
 */

import { css, Global, useTheme } from '@emotion/react';

export const GlobalStyles = () => {
  const theme = useTheme();

  return (
    <Global
      styles={css`
        /* ========================================
         * Global Reset & Base Styles ONLY
         * ======================================== */
        *,
        *::before,
        *::after {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        html {
          font-size: 16px;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          height: 100%;
          overflow: hidden;
        }

        body {
          font-family:
            'Inter',
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            Roboto,
            sans-serif;
          line-height: 1.5;
          height: 100%;
          overflow: hidden;
          color: ${theme.colors.text.primary};
          background-color: ${theme.colors.surface.layout.background};
        }

        #root {
          height: 100%;
          overflow: hidden;
        }

        /* Remove default list styles */
        ul,
        ol {
          list-style: none;
        }

        /* Remove default link styles */
        a {
          text-decoration: none;
          color: inherit;
        }

        /* Remove default button styles */
        button {
          font-family: inherit;
          cursor: pointer;
          border: none;
          background: none;
        }

        /* Improve image defaults */
        img,
        picture,
        video,
        canvas,
        svg {
          display: block;
          max-width: 100%;
        }

        /* Remove default input styles */
        input,
        textarea,
        select {
          font-family: inherit;
        }

        /* Remove default table styles */
        table {
          border-collapse: collapse;
          border-spacing: 0;
        }

        /* Custom scrollbar styles */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        ::-webkit-scrollbar-track {
          background: ${theme.colors.control.scroll.gutter};
          border: none;
        }

        ::-webkit-scrollbar-thumb {
          background: ${theme.colors.control.scroll.handgrip};
          border-radius: 4px;
          border: none;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: ${theme.colors.control.scroll.handgrip};
          opacity: 0.8;
        }

        /* Firefox scrollbar styles */
        * {
          scrollbar-width: thin;
          scrollbar-color: ${theme.colors.control.scroll.handgrip} ${theme.colors.control.scroll.gutter};
        }

        /* ========================================
         * Sonner Toast Custom Styles
         * ======================================== */
        [data-sonner-toaster] {
          font-family:
            'Inter',
            -apple-system,
            BlinkMacSystemFont,
            'Segoe UI',
            Roboto,
            sans-serif;
        }

        [data-sonner-toast] {
          display: flex;
          width: 320px;
          padding: 12px 16px;
          align-items: center;
          gap: 12px;
          border-radius: 8px;
          border: 1px solid;
          box-shadow:
            0 4px 8px 0 rgba(0, 0, 0, 0.08),
            0 8px 24px 0 rgba(0, 0, 0, 0.06);
        }

        /* 번역 문자열의 \n 줄바꿈을 토스트에서 그대로 표시 */
        [data-sonner-toast] [data-title],
        [data-sonner-toast] [data-description] {
          white-space: pre-line;
        }

        /* Info Toast */
        [data-sonner-toast][data-type='info'] {
          border-color: ${theme.colors.status.calib.stroke.border};
          background: ${theme.colors.status.calib.stroke.surface};
        }

        [data-sonner-toast][data-type='info'] [data-title] {
          color: ${theme.colors.status.calib.stroke.text};
          font-size: 14px;
          font-weight: 500;
          line-height: 120%;
        }

        [data-sonner-toast][data-type='info'] [data-description] {
          color: ${theme.colors.status.calib.stroke.text};
          font-size: 14px;
          font-weight: 400;
          line-height: 120%;
        }

        /* Success Toast */
        [data-sonner-toast][data-type='success'] {
          border-color: ${theme.colors.status.stable.stroke.border};
          background: ${theme.colors.status.stable.stroke.surface};
        }

        [data-sonner-toast][data-type='success'] [data-title] {
          color: ${theme.colors.status.stable.stroke.text};
          font-size: 14px;
          font-weight: 500;
          line-height: 120%;
        }

        [data-sonner-toast][data-type='success'] [data-description] {
          color: ${theme.colors.status.stable.stroke.text};
          font-size: 14px;
          font-weight: 400;
          line-height: 120%;
        }

        /* Error Toast */
        [data-sonner-toast][data-type='error'] {
          border-color: ${theme.colors.status.danger.stroke.border};
          background: ${theme.colors.status.danger.stroke.surface};
        }

        [data-sonner-toast][data-type='error'] [data-title] {
          color: ${theme.colors.status.danger.stroke.text};
          font-size: 14px;
          font-weight: 500;
          line-height: 120%;
        }

        [data-sonner-toast][data-type='error'] [data-description] {
          color: ${theme.colors.status.danger.stroke.text};
          font-size: 14px;
          font-weight: 400;
          line-height: 120%;
        }

        /* Warning Toast */
        [data-sonner-toast][data-type='warning'] {
          border-color: ${theme.colors.status.caution.stroke.border};
          background: ${theme.colors.status.caution.fill.surface};
        }

        [data-sonner-toast][data-type='warning'] [data-title] {
          color: ${theme.colors.status.caution.stroke.text};
          font-size: 14px;
          font-weight: 500;
          line-height: 120%;
        }

        [data-sonner-toast][data-type='warning'] [data-description] {
          color: ${theme.colors.status.caution.stroke.text};
          font-size: 14px;
          font-weight: 400;
          line-height: 120%;
        }

        /* Icon styling */
        [data-sonner-toast] [data-icon] {
          color: inherit;
        }

        /* Push Notification Toast — 화면 중앙, 확대 */
        [data-sonner-toast].push-notification-toast {
          width: 420px;
          padding: 18px 24px;
          gap: 16px;
          border-radius: 12px;
          box-shadow:
            0 8px 16px 0 rgba(0, 0, 0, 0.12),
            0 16px 48px 0 rgba(0, 0, 0, 0.1);
        }

        [data-sonner-toast].push-notification-toast [data-title] {
          font-size: 18px !important;
          font-weight: 600 !important;
          line-height: 140% !important;
        }

        [data-sonner-toast].push-notification-toast [data-description] {
          font-size: 15px !important;
          font-weight: 400 !important;
          line-height: 150% !important;
          margin-top: 4px;
        }

        [data-sonner-toast].push-notification-toast [data-icon] {
          width: 24px;
          height: 24px;
        }

        [data-sonner-toast].push-notification-toast [data-icon] svg {
          width: 24px;
          height: 24px;
        }

        /* ========================================
         * END OF GLOBAL STYLES
         * 
         * 🚫 이 아래에 아무것도 추가하지 마세요!
         * 모든 추가 스타일은 ui/components/ 또는
         * 각 페이지의 styled-components를 사용하세요.
         * ======================================== */
      `}
    />
  );
};
